import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'redux/store';
import { Button, ButtonGroup, Form, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { RootState } from '../../../../redux/store';
import { fetchWorkflowDetails, markWorkflowAsRead, respondToWorkflowAction, saveWorkflowAction } from '../../../../redux/slices/workflowsSlice';
import { selectHour12Option } from 'redux/selectors/userSettings';

type Props = {
  workflow_id: number;
  markAsRead?: boolean;
  onUpdated?: () => void;
};

const WorkflowDetailsPanel: React.FC<Props> = ({ workflow_id, markAsRead: shouldMarkAsRead = true, onUpdated }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { workflows, detailsLoading } = useSelector((state: RootState) => state.workflows);

  const hour12 = useSelector(selectHour12Option);

  const selectedWorkflow = useMemo(() => workflows.find((wf) => wf.workflow_id === workflow_id), [workflows, workflow_id]);

  const [inputValues, setInputValues] = useState<{
    [key: number]: { [label: string]: string };
  }>({});

  const [showSaveButton, setShowSaveButton] = useState<{
    [key: number]: boolean;
  }>({});

  useEffect(() => {
    if (!workflow_id) return;

    dispatch(fetchWorkflowDetails(workflow_id));

    if (shouldMarkAsRead) {
      dispatch(markWorkflowAsRead(workflow_id));
    }

    setInputValues({});
    setShowSaveButton({});
  }, [dispatch, workflow_id, shouldMarkAsRead]);

  useEffect(() => {
    if (!selectedWorkflow) return;

    const initial: { [key: number]: { [label: string]: string } } = {};

    selectedWorkflow.workflows_actions?.forEach((action) => {
      initial[action.workflow_action_id] = {};

      if (Array.isArray(action.response_inputs)) {
        action.response_inputs.forEach((input) => {
          initial[action.workflow_action_id][input.label] = input.value;
        });
      }

      if (Array.isArray(action.dropdown_parameters)) {
        action.dropdown_parameters.forEach((param) => {
          const seed = (param.out_value ?? param.value ?? param.submit_value ?? param.default_expression ?? '').toString().trim();

          initial[action.workflow_action_id][param.name] = seed;
        });
      }
    });

    setInputValues(initial);
  }, [selectedWorkflow]);

  const handleInputChange = (workflow_action_id: number, label: string, value: string) => {
    setInputValues((prev) => ({
      ...prev,
      [workflow_action_id]: {
        ...(prev[workflow_action_id] || {}),
        [label]: value,
      },
    }));

    const updatedValues = {
      ...(inputValues[workflow_action_id] || {}),
      [label]: value,
    };

    setShowSaveButton((prev) => ({
      ...prev,
      [workflow_action_id]: Object.values(updatedValues).some((v) => typeof v === 'string' && v.trim() !== ''),
    }));
  };

  const handleSave = (workflow_action_id: number) => {
    const allValues = inputValues[workflow_action_id] || {};

    const action = selectedWorkflow?.workflows_actions.find((a) => a.workflow_action_id === workflow_action_id);

    const response_inputs = (action?.response_input_options || []).map((opt) => ({
      label: opt.label,
      value: allValues[opt.label] || '',
    }));

    const dropdown_parameters = (action?.dropdown_parameters || []).map((param) => ({
      parameter_id: param.parameter_id,
      value: allValues[param.name] ?? param.out_value ?? param.value ?? param.default_expression ?? '',
    }));

    dispatch(
      saveWorkflowAction({
        workflow_action_id,
        response_inputs,
        dropdown_parameters,
      })
    );

    setShowSaveButton((prev) => ({ ...prev, [workflow_action_id]: false }));
    onUpdated?.();
  };

  const onRespond = async (workflow_action_id: number, chosen_id: string | number) => {
    await dispatch(
      respondToWorkflowAction({
        workflow_action_id,
        chosen_id,
      })
    );

    dispatch(fetchWorkflowDetails(workflow_id));
    onUpdated?.();
  };

  return (
    <div
      style={{
        padding: '0.5rem',
        overflow: 'auto',
        height: '100%',
        minHeight: 0,
        flexGrow: 1,
      }}
    >
      {detailsLoading && !selectedWorkflow ? (
        <Spinner animation="border" />
      ) : !selectedWorkflow ? (
        <p className="m-0">{t('workflows.no_selected_workflow')}</p>
      ) : (
        <>
          <div className="mb-4">
            <h3>WORKFLOW {selectedWorkflow.workflow_id}</h3>

            <p>
              <strong>{t('subject')}: </strong>
              {selectedWorkflow.workflow_subject}
            </p>

            <p>
              <strong>{t('category')}: </strong>
              {selectedWorkflow.workflow_category}
            </p>

            <p>
              <strong>{t('created_at')}: </strong>
              {new Date(selectedWorkflow.created_at).toLocaleString('nl-NL', {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12,
              })}
            </p>

            <p>
              <strong>{t('workflows.last_updated')}: </strong>
              {new Date(selectedWorkflow.updated_at).toLocaleString('nl-NL', {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12,
              })}
            </p>
          </div>

          <Table hover size="sm" style={{ tableLayout: 'fixed', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: '80px' }}>ID</th>
                <th style={{ width: '150px' }}>{t('date')}</th>
                <th style={{ width: 'auto' }}>Details</th>
              </tr>
            </thead>

            <tbody>
              {selectedWorkflow.workflows_actions.map((action) => (
                <tr key={action.workflow_action_id}>
                  <td style={{ width: '80px' }}>{action.workflow_action_id}</td>

                  <td style={{ width: '150px' }}>
                    {new Date(action.created_at).toLocaleString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12,
                    })}
                  </td>

                  <td
                    style={{
                      width: 'auto',
                      maxWidth: '0',
                      wordWrap: 'break-word',
                    }}
                  >
                    {action.response ? (
                      <>
                        <p>
                          <strong>{t('workflows.chosen_option')}: </strong>
                          {action.response.label} ({action.response.user}{' '}
                          {new Date(action.response.timestamp).toLocaleString('nl-NL', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12,
                          })}
                          )
                        </p>

                        {Array.isArray(action.response_inputs) && action.response_inputs.length > 0 && (
                          <div>
                            {action.response_inputs.map((input) => (
                              <Form.Group key={input.label} className="mb-2">
                                <Form.Label>{input.label}</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={3}
                                  disabled
                                  value={input.value || t('empty')}
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    color: '#495057',
                                  }}
                                />
                              </Form.Group>
                            ))}
                          </div>
                        )}

                        {action.dropdown_parameters && action.dropdown_parameters.length > 0 && (
                          <div className="mt-3">
                            {action.dropdown_parameters.map((param) => {
                              const originalVal = param.value ?? param.default_expression ?? '';
                              const selectedVal = param.out_value ?? param.value ?? param.default_expression ?? '';
                              const hasChanged = param.out_value && param.out_value !== param.value;

                              return (
                                <p key={param.parameter_id}>
                                  <strong>{param.name}: </strong>
                                  {hasChanged ? (
                                    <span>
                                      <span
                                        style={{
                                          textDecoration: 'line-through',
                                          color: '#666',
                                        }}
                                      >
                                        {originalVal || t('empty')}
                                      </span>
                                      {' → '}
                                      <span
                                        style={{
                                          color: '#28a745',
                                          fontWeight: 'bold',
                                        }}
                                      >
                                        {selectedVal || t('empty')}
                                      </span>
                                    </span>
                                  ) : (
                                    selectedVal || t('empty')
                                  )}
                                </p>
                              );
                            })}
                          </div>
                        )}

                        <p>
                          <strong>{t('workflows.original_message')}: </strong>
                          <br />
                        </p>

                        <p>
                          <strong>{t('subject')}: </strong>
                          {action.subject || t('empty')}
                        </p>

                        <p>
                          <strong>{t('message')}: </strong>
                          <br />
                          <div
                            style={{
                              maxWidth: '100%',
                              overflow: 'hidden',
                              display: 'block',
                              width: '100%',
                              boxSizing: 'border-box',
                            }}
                            dangerouslySetInnerHTML={{
                              __html: (action.message || t('empty'))
                                .replace(/\n/g, '<br>')
                                .replace(
                                  /<div style="overflow-x: auto; max-width: 100%;">/g,
                                  '<div style="overflow-x: auto; max-width: 100%; width: 100%; display: block; box-sizing: border-box; position: relative;">'
                                )
                                .replace(
                                  /<table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; min-width: 100%;">/g,
                                  '<table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: max-content; min-width: 100%; table-layout: fixed; white-space: nowrap;">'
                                ),
                            }}
                          />
                        </p>
                      </>
                    ) : (
                      <div>
                        <div className="d-flex justify-content-between">
                          <strong>{t('workflows.chosen_option')}: </strong>

                          <div className="ms-auto">
                            <ButtonGroup>
                              {action.response_options?.map((option) => (
                                <Button
                                  key={option.id}
                                  variant="outline-primary"
                                  onClick={async () => {
                                    await handleSave(action.workflow_action_id);
                                    await onRespond(action.workflow_action_id, option.id);
                                  }}
                                >
                                  {option.label}
                                </Button>
                              ))}
                            </ButtonGroup>
                          </div>
                        </div>

                        <p>
                          <strong>{t('subject')}: </strong>
                          {action.subject || t('empty')}
                        </p>

                        <p>
                          <strong>{t('message')}: </strong>
                          <br />
                          <div
                            style={{
                              maxWidth: '100%',
                              overflow: 'hidden',
                              display: 'block',
                              width: '100%',
                              boxSizing: 'border-box',
                            }}
                            dangerouslySetInnerHTML={{
                              __html: (action.message || t('empty'))
                                .replace(/\n/g, '<br>')
                                .replace(
                                  /<div style="overflow-x: auto; max-width: 100%;">/g,
                                  '<div style="overflow-x: auto; max-width: 100%; width: 100%; display: block; box-sizing: border-box; position: relative;">'
                                )
                                .replace(
                                  /<table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; min-width: 100%;">/g,
                                  '<table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: max-content; min-width: 100%; table-layout: fixed; white-space: nowrap;">'
                                ),
                            }}
                          />
                        </p>

                        {action.dropdown_parameters && action.dropdown_parameters.length > 0 && (
                          <div className="mt-3">
                            {action.dropdown_parameters.map((param) => {
                              const options = String(param.options || '')
                                .split(',')
                                .map((opt) => opt.trim())
                                .filter(Boolean);

                              const currentVal =
                                inputValues[action.workflow_action_id]?.[param.name] ?? param.out_value ?? param.value ?? param.default_expression ?? '';

                              return (
                                <Form.Group key={param.parameter_id} className="mb-2">
                                  <Form.Label>{param.name}</Form.Label>
                                  <Form.Select
                                    id={`${action.workflow_action_id}-${param.name}`}
                                    value={currentVal}
                                    onChange={(e) => handleInputChange(action.workflow_action_id, param.name, e.target.value)}
                                  >
                                    {options.map((opt, idx) => (
                                      <option key={idx} value={opt}>
                                        {opt}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </Form.Group>
                              );
                            })}

                            {showSaveButton[action.workflow_action_id] && (
                              <Button variant="primary" onClick={() => handleSave(action.workflow_action_id)}>
                                {t('save')}
                              </Button>
                            )}
                          </div>
                        )}

                        {action.response_input_options && action.response_input_options.length > 0 && (
                          <div>
                            {action.response_input_options.map((opt) => (
                              <Form.Group key={opt.label} className="mb-2">
                                <Form.Label>{opt.label}</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={3}
                                  id={`${action.workflow_action_id}-${opt.label}`}
                                  value={inputValues[action.workflow_action_id]?.[opt.label] || ''}
                                  onChange={(e) => handleInputChange(action.workflow_action_id, opt.label, e.target.value)}
                                />
                              </Form.Group>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </div>
  );
};

export default WorkflowDetailsPanel;
